import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Collapse, Link, Tooltip, Typography } from "@mui/material";
import { BookOpen, ChevronDown, ExternalLink } from "lucide-react";
import { getSourceLabel } from "../../utils/citations";

/**
 * Knowledge-base sources behind an assistant answer, rendered under the message.
 *
 * Only `id` is guaranteed on each entry — every other field can be null, so each
 * one is rendered only when present and `name` falls back to "Untitled source".
 */
const AnswerSources = ({ sources, isDarkMode, highlightedId, onHighlightHandled }) => {
  const [expanded, setExpanded] = useState(false);
  const rowRefs = useRef({});

  const hasSources = Array.isArray(sources) && sources.length > 0;

  // Clicking a `[Sn]` chip in the answer opens the panel and jumps to that row.
  useEffect(() => {
    if (!highlightedId || !hasSources) return;

    setExpanded(true);
    const node = rowRefs.current[highlightedId];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }

    const timer = setTimeout(() => onHighlightHandled?.(), 1600);
    return () => clearTimeout(timer);
  }, [highlightedId, hasSources, onHighlightHandled]);

  const formattedSources = useMemo(() => {
    if (!hasSources) return [];

    return sources.map((source, index) => {
      const details = [];
      if (source?.group) details.push(String(source.group));
      if (Number.isFinite(Number(source?.page))) details.push(`Page ${source.page}`);
      if (Number.isFinite(Number(source?.chunk))) details.push(`Chunk ${source.chunk}`);

      const score = Number(source?.score);

      return {
        key: source?.id || `source-${index}`,
        id: source?.id,
        position: index + 1,
        label: getSourceLabel(source),
        details: details.join(" · "),
        score: Number.isFinite(score) ? score.toFixed(2) : null,
        url: typeof source?.source_url === "string" && source.source_url ? source.source_url : null,
      };
    });
  }, [sources, hasSources]);

  // No sources → no frame from the backend → nothing to render.
  if (!hasSources) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: isDarkMode ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)",
        backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "text.secondary",
          font: "inherit",
          textAlign: "left",
        }}
      >
        <BookOpen size={14} strokeWidth={2} aria-hidden />
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.3px" }}>
          {formattedSources.length} source{formattedSources.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-flex",
            transition: "transform 160ms ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        >
          <ChevronDown size={14} strokeWidth={2} />
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {formattedSources.map((source) => {
            const isHighlighted = Boolean(source.id) && source.id === highlightedId;

            return (
              <Box
                key={source.key}
                ref={(node) => {
                  if (source.id) rowRefs.current[source.id] = node;
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1,
                  borderRadius: "8px",
                  transition: "background-color 200ms ease",
                  backgroundColor: isHighlighted
                    ? isDarkMode
                      ? "rgba(227,98,80,0.22)"
                      : "rgba(227,98,80,0.12)"
                    : "transparent",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: "#ffffff",
                    backgroundColor: "#e36250",
                  }}
                >
                  {source.position}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Tooltip title={source.label} placement="top-start">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {source.label}
                    </Typography>
                  </Tooltip>
                  {source.details ? (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {source.details}
                    </Typography>
                  ) : null}
                </Box>

                {source.score ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={source.score}
                    sx={{ height: 20, fontSize: "0.68rem", flexShrink: 0 }}
                  />
                ) : null}

                {source.url ? (
                  <Link
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${source.label}`}
                    sx={{ display: "inline-flex", flexShrink: 0, color: "text.secondary", mt: "2px" }}
                  >
                    <ExternalLink size={14} strokeWidth={2} aria-hidden />
                  </Link>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AnswerSources;
